import {
  Search, LayoutGrid, List, SlidersHorizontal, ChevronDown, Cable,
} from 'lucide-react'
import { CONNECTIVITY_TABS, STATUS_OPTIONS, SORT_OPTIONS } from '../../data/apiConnectData'

export default function IntegrationToolbar({
  search,
  onSearchChange,
  activeTab,
  onTabChange,
  categoryFilter,
  onCategoryFilterChange,
  statusFilter,
  onStatusFilterChange,
  sortBy,
  onSortChange,
  view,
  onViewChange,
  categories,
  resultCount,
}) {
  return (
    <div className="sticky top-0 z-20 space-y-4 rounded-2xl border border-slate-500 bg-white/95 p-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)] backdrop-blur-md dark:border-slate-600 dark:bg-slate-900/95 sm:p-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative min-w-0 flex-1 group">
          <div className="relative flex items-center overflow-hidden rounded-xl border border-slate-500 bg-slate-100 transition-all duration-200 focus-within:border-brand-400/80 focus-within:shadow-[0_0_0_4px_rgba(37,99,235,0.12)] dark:border-slate-600 dark:bg-slate-800 dark:focus-within:border-brand-500/60 dark:focus-within:shadow-[0_0_0_4px_rgba(37,99,235,0.18)]">
            <div className="flex h-11 w-10 shrink-0 items-center justify-center">
              <Search className="h-4 w-4 text-slate-400 transition-colors group-focus-within:text-brand-500 dark:text-slate-500 dark:group-focus-within:text-brand-400" />
            </div>
            <input
              type="search"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search integrations by name, type, or tag…"
              className="h-11 w-full border-0 bg-transparent pr-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none dark:text-white dark:placeholder:text-slate-500"
              aria-label="Search integrations"
            />
          </div>
        </div>

        <div
          className="inline-flex shrink-0 rounded-xl bg-slate-100 p-1 ring-1 ring-slate-200/60 dark:bg-slate-800 dark:ring-slate-700/60"
          role="tablist"
          aria-label="Connectivity mode"
        >
          {CONNECTIVITY_TABS.map((tab) => {
            const active = activeTab === tab.id
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onTabChange(tab.id)}
                className={[
                  'inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold transition-all duration-200 sm:text-[13px]',
                  active
                    ? 'bg-white text-brand-700 shadow-sm ring-1 ring-slate-200/80 dark:bg-slate-700 dark:text-brand-300 dark:ring-slate-600/80'
                    : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white',
                ].join(' ')}
              >
                {tab.id === 'api' ? <Cable className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      <div className="flex flex-col gap-3 border-t border-slate-200 pt-4 dark:border-slate-700 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex flex-wrap items-center gap-2">
          <FilterSelect
            label="Category"
            value={categoryFilter}
            onChange={onCategoryFilterChange}
            options={[
              { value: 'all', label: 'All categories' },
              ...categories.map((c) => ({ value: c.id, label: c.title })),
            ]}
          />
          <FilterSelect
            label="Status"
            value={statusFilter}
            onChange={onStatusFilterChange}
            options={STATUS_OPTIONS.map((o) => ({ value: o.id, label: o.label }))}
          />
          <FilterSelect
            label="Sort"
            value={sortBy}
            onChange={onSortChange}
            options={SORT_OPTIONS.map((o) => ({ value: o.id, label: o.label }))}
          />
        </div>

        <div className="flex items-center gap-2 sm:ml-auto">
          <span className="text-xs text-slate-500 dark:text-slate-400">
            <span className="font-semibold text-slate-800 dark:text-slate-200">{resultCount}</span> results
          </span>
          <div className="inline-flex rounded-lg bg-slate-100 p-0.5 ring-1 ring-slate-200/60 dark:bg-slate-800 dark:ring-slate-700/60" role="group" aria-label="View mode">
            <ViewButton active={view === 'grid'} onClick={() => onViewChange('grid')} label="Grid view">
              <LayoutGrid className="h-4 w-4" />
            </ViewButton>
            <ViewButton active={view === 'list'} onClick={() => onViewChange('list')} label="List view">
              <List className="h-4 w-4" />
            </ViewButton>
          </div>
        </div>
      </div>
    </div>
  )
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <label className="inline-flex cursor-pointer">
      <span className="sr-only">{label}</span>
      <div className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-500 bg-white px-3 shadow-[0_1px_2px_rgba(15,23,42,0.03)] transition-colors hover:border-slate-400 focus-within:border-brand-400 focus-within:ring-2 focus-within:ring-brand-500/15 dark:border-slate-600 dark:bg-slate-800 dark:hover:border-slate-500 dark:focus-within:border-brand-500/60 dark:focus-within:ring-brand-500/20">
        <SlidersHorizontal className="h-3.5 w-3.5 shrink-0 text-slate-400 dark:text-slate-500" aria-hidden />
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="cursor-pointer appearance-none border-0 bg-transparent py-0 pr-0.5 text-xs font-semibold leading-none text-slate-800 outline-none focus:ring-0 dark:text-slate-200"
          aria-label={label}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400 dark:text-slate-500" aria-hidden />
      </div>
    </label>
  )
}

function ViewButton({ active, onClick, label, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      className={[
        'rounded-md p-2 transition-colors',
        active
          ? 'bg-white text-brand-700 shadow-sm dark:bg-slate-700 dark:text-brand-300'
          : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white',
      ].join(' ')}
    >
      {children}
    </button>
  )
}
